import type { IntranetMenuBody } from '../api/types/intranet.types';
import { ApiService } from '../api/services/ApiService';
import type { UserBody, UserTodo, WhoAmIBody } from '../api/types/users.types';
import { ServiceRegistry } from './ServiceRegistry';

class UserState implements WhoAmIBody {
    public isLoggedIn: boolean = $state(false);
    public is_root: boolean = $state(false);
    public is_admin: boolean = $state(false);
    public is_user: boolean = $state(false);
    public readable: string[] = $state([]);
    public intranets: IntranetMenuBody[] = $state([]);
    public todo: UserTodo[] = $state([]);
    public user: UserBody | undefined = $state();
    public tfa_app_deadline: string | null = $state(null);
}

class UserServiceClass {
    private readonly PollIntervalMs: number = 10000;
    /** Mirrors UserDealer::REGISTER_COLOR_MAX (PHP). */
    private readonly RandomColorMax = 0x60;

    private readonly State: UserState = new UserState();
    private PollTimer: ReturnType<typeof setTimeout> | null = null;
    private IsStopped: boolean = false;
    private readonly HandleVisibility = (): void => {
        if (document.hidden) {
            this.clearPollTimer();
            return;
        }
        void this.poll();
    };

    public constructor() {
        void this.load(true);
        this.startWatching();
    }

    public get body(): Readonly<WhoAmIBody> {
        return this.State;
    }

    /** The name to show of the logged-in user (getDisplayName()); empty while logged out. */
    public get displayName(): string {
        return this.State.user ? this.getDisplayName(this.State.user) : '';
    }

    /** Member of ROOT — the root area. */
    public get isRoot(): boolean {
        return this.State.isLoggedIn && this.State.is_root;
    }

    /** Member of ADMIN or ROOT — the admin area. */
    public get isAdmin(): boolean {
        return this.State.isLoggedIn && this.State.is_admin;
    }

    /** Member of USER (or above); logged in without it only logout and the password are open. */
    public get isUser(): boolean {
        return this.State.isLoggedIn && this.State.is_user;
    }

    /** Whether the user can read at least one record of the type (`ERightResource`). */
    public canRead(resource: string): boolean {
        return this.State.readable.includes(resource);
    }

    /** The deadline until which a TFA app must be linked, or null. */
    public get tfaAppDeadline(): string | null {
        return this.State.tfa_app_deadline;
    }

    /** A random dark `#rrggbb` for white text — each channel up to RandomColorMax, like the registration (PHP). */
    public createRandomColor(): string {
        const Channel = (): string =>
            Math.floor(Math.random() * (this.RandomColorMax + 1))
                .toString(16)
                .padStart(2, '0');

        return `#${Channel()}${Channel()}${Channel()}`;
    }

    /** The name to show of any user: nickname, else given name, else user name — the bar, tables, greetings. */
    public getDisplayName(user: { username: string; nickname?: string | null; given_name?: string | null }): string {
        return user.nickname ?? user.given_name ?? user.username;
    }

    /** Ends polling and listening for good — the replaced instance after a Vite HMR update (`ServiceRegistry`). */
    public stop(): void {
        this.IsStopped = true;
        this.clearPollTimer();
        document.removeEventListener('visibilitychange', this.HandleVisibility);
    }

    /** Asks the API again at once, after a login or logout. */
    public async refresh(): Promise<void> {
        await this.load(true);
    }

    protected async load(refresh = false): Promise<void> {
        const Result = await ApiService.users.whoAmI(refresh);
        if (this.IsStopped || Result.body === undefined) {
            return;
        }

        // -
        this.State.isLoggedIn = Result.body.isLoggedIn;
        this.State.is_root = Result.body.is_root;
        this.State.is_admin = Result.body.is_admin;
        this.State.is_user = Result.body.is_user;
        this.State.readable = Result.body.readable;
        this.State.intranets = Result.body.intranets;
        this.State.todo = Result.body.todo;
        this.State.user = Result.body.user;
        this.State.tfa_app_deadline = Result.body.tfa_app_deadline ?? null;
    }

    protected startWatching(): void {
        document.addEventListener('visibilitychange', this.HandleVisibility);

        // -
        this.schedulePoll();
    }

    private async poll(): Promise<void> {
        this.clearPollTimer();
        await this.load(true);
        this.schedulePoll();
    }

    private schedulePoll(): void {
        this.clearPollTimer();
        if (this.IsStopped || document.hidden) {
            return;
        }

        // -
        this.PollTimer = setTimeout((): void => {
            void this.poll();
        }, this.PollIntervalMs);
    }

    private clearPollTimer(): void {
        if (this.PollTimer !== null) {
            clearTimeout(this.PollTimer);
            this.PollTimer = null;
        }
    }
}

export const UserService = ServiceRegistry.register('UserService', new UserServiceClass());
